/**
 * AutoLogout
 *
 * - Active users are never logged out.
 * - After 30 minutes of true idle (no mouse/keyboard/scroll/touch), a
 *   warning overlay appears with a 2-minute countdown.
 * - Any mouse movement while the warning is visible immediately closes it
 *   and resets the idle clock — no button required.
 * - If the user stays completely idle through the entire countdown they
 *   are logged out.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Logout } from '../../store/actions/AuthActions';

const IDLE_MINUTES     = 30;
const WARN_SECONDS     = 120;   // 2-minute warning
const IDLE_MS          = IDLE_MINUTES * 60 * 1000;
const WARN_MS          = WARN_SECONDS * 1000;

// Events that count as "activity" for idle-reset purposes.
// mousemove is separated below so we can also dismiss the warning with it.
const RESET_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

export default function AutoLogout() {
    const dispatch    = useDispatch();
    const navigate    = useNavigate();

    const [showWarning, setShowWarning] = useState(false);
    const [countdown,   setCountdown]   = useState(WARN_SECONDS);

    const logoutTimerRef   = useRef(null);
    const warnTimerRef     = useRef(null);
    const countdownRef     = useRef(null);
    const warningActiveRef = useRef(false);   // mirrors showWarning without stale-closure issues

    const doLogout = useCallback(() => {
        warningActiveRef.current = false;
        setShowWarning(false);
        clearInterval(countdownRef.current);
        dispatch(Logout(navigate));
    }, [dispatch, navigate]);

    const clearAllTimers = useCallback(() => {
        clearTimeout(logoutTimerRef.current);
        clearTimeout(warnTimerRef.current);
        clearInterval(countdownRef.current);
    }, []);

    const resetTimer = useCallback(() => {
        clearAllTimers();
        warningActiveRef.current = false;
        setShowWarning(false);
        setCountdown(WARN_SECONDS);

        // Show warning 2 minutes before the idle deadline
        warnTimerRef.current = setTimeout(() => {
            warningActiveRef.current = true;
            setShowWarning(true);
            setCountdown(WARN_SECONDS);

            countdownRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            // Log out when countdown reaches zero
            logoutTimerRef.current = setTimeout(doLogout, WARN_MS);
        }, Math.max(IDLE_MS - WARN_MS, 0));
    }, [clearAllTimers, doLogout]);

    // Mouse movement: if the warning is visible, dismiss it and reset the clock.
    // If the warning is not visible, this is just normal activity — resetTimer
    // is already bound to the other RESET_EVENTS so we only need to handle the
    // "dismiss warning" case here to avoid resetting the clock on every pixel.
    const handleMouseMove = useCallback(() => {
        if (warningActiveRef.current) {
            resetTimer();
        }
    }, [resetTimer]);

    useEffect(() => {
        resetTimer();

        RESET_EVENTS.forEach(evt =>
            window.addEventListener(evt, resetTimer, { passive: true }),
        );
        window.addEventListener('mousemove', handleMouseMove, { passive: true });

        return () => {
            clearAllTimers();
            RESET_EVENTS.forEach(evt =>
                window.removeEventListener(evt, resetTimer),
            );
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [clearAllTimers, handleMouseMove, resetTimer]);

    if (!showWarning) return null;

    const formattedCountdown = countdown >= 60
        ? `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')}`
        : `${countdown}s`;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <style>{`
                .autologout-card.card {
                    height: auto !important;
                    margin-bottom: 0 !important;
                }
                .autologout-card .card-header {
                    background: #dc3545 !important;
                    background-color: #dc3545 !important;
                    color: #fff !important;
                    border-bottom: 0 !important;
                    border-radius: 0.75rem 0.75rem 0 0 !important;
                    padding: 0.5rem 1rem !important;
                }
            `}</style>
            <div className="card shadow autologout-card" style={{ width: 360, maxWidth: '95vw' }}>
                <div className="card-header d-flex align-items-center gap-2">
                    <i className="las la-clock" style={{ fontSize: '1.1rem' }} />
                    <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Session Timeout Warning</span>
                </div>
                <div className="card-body text-center py-3 px-3">
                    <p style={{ fontSize: '0.85rem', marginBottom: 4 }}>
                        No activity detected. You will be logged out in:
                    </p>
                    <div
                        style={{
                            fontSize: '1.6rem',
                            fontWeight: 700,
                            color: countdown <= 30 ? '#dc3545' : undefined,
                            letterSpacing: 1,
                            lineHeight: 1.1,
                            marginBottom: 4,
                        }}
                    >
                        {formattedCountdown}
                    </div>
                    <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: 0 }}>
                        Move the cursor to stay logged in.
                    </p>
                </div>
            </div>
        </div>
    );
}
